import React, { useEffect, useState, Children } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Layout } from '../components/Layout';
import { QuoteWidget } from '../components/QuoteWidget';
import { MarqueeGallery } from '../components/MarqueeGallery';
import { TransformationSlider } from '../components/TransformationSlider';
import { ProcessGrid } from '../components/ProcessGrid';
import { Showroom } from '../components/Showroom';
import { ValueProp } from '../components/ValueProp';
import { FacebookIcon, InstagramIcon, TwitterIcon, PhoneIcon, MailIcon, MapPinIcon } from 'lucide-react';
const HERO_IMAGES = ['https://cdn.magicpatterns.com/uploads/uyAyGkhhVAJn3hZ2c4Gr3n/image.png', 'https://images.unsplash.com/photo-1588854337236-6889d631faa8?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&q=80&w=800', 'https://images.pexels.com/photos/1080721/pexels-photo-1080721.jpeg?auto=compress&cs=tinysrgb&w=800', 'https://images.pexels.com/photos/8186477/pexels-photo-8186477.jpeg?auto=compress&cs=tinysrgb&w=800'];
const TESTIMONIALS = [{
  name: 'Jack Smith',
  text: 'Nk home improvement has a great team, friendly relation, they designed the ideal house for my needs. it is a fantastic project, we are very happy! they were originally recommended to us by a friend and now we recommend them.',
  image: 'https://i.pravatar.cc/150?u=jack'
}, {
  name: 'Sarah Johnson',
  text: 'The cabinet refacing was seamless. They transformed our kitchen in just two days without the mess of a full remodel. Highly professional and detail-oriented team.',
  image: 'https://i.pravatar.cc/150?u=sarah'
}, {
  name: 'Michael Chen',
  text: 'Excellent workmanship on our bathroom flooring. The attention to detail with the tiling was impressive. NK Resurfacers truly delivers on their promise of quality.',
  image: 'https://i.pravatar.cc/150?u=michael'
}];
const PROJECTS = [{
  title: 'Traditional Oak Reface',
  location: 'Bethesda, MD',
  image: 'https://cdn.magicpatterns.com/uploads/fZacsfF1XsKzt9rhx3HW2f/b-1.jpg'
}, {
  title: 'Modern Kitchen Update',
  location: 'Arlington, VA',
  image: 'https://cdn.magicpatterns.com/uploads/jSif27kDeCfjpi6GVtuyf2/b-2.jpg'
}, {
  title: 'Classic Cabinet Refinish',
  location: 'Potomac, MD',
  image: 'https://cdn.magicpatterns.com/uploads/v1Qn8imjfgNzCQUpAGimmM/b-3.jpg'
}, {
  title: 'Vintage Wood Restoration',
  location: 'Silver Spring, MD',
  image: 'https://cdn.magicpatterns.com/uploads/tfJ5piQs4i3CyZKkHGeSNj/b-4.jpg'
}, {
  title: 'Rustic Kitchen Remodel',
  location: 'Bowie, MD',
  image: 'https://cdn.magicpatterns.com/uploads/iXZhiea3EJJkBE99fJvRUh/b-5.jpg'
}];
export function LandingPage() {
  const [currentImage, setCurrentImage] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage(prev => (prev + 1) % HERO_IMAGES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  const nextTestimonial = () => {
    setCurrentTestimonial(prev => (prev + 1) % TESTIMONIALS.length);
  };
  const prevTestimonial = () => {
    setCurrentTestimonial(prev => (prev - 1 + TESTIMONIALS.length) % TESTIMONIALS.length);
  };
  return <Layout data-id="element-382">
      {/* Hero Section */}
      <header className="relative h-[85vh] flex items-center overflow-hidden" data-id="element-383">
        {/* Background Image - Darker, Modern Kitchen */}
        <div className="absolute inset-0 z-0" data-id="element-384">
          <img src="https://images.unsplash.com/photo-1556912173-3bb406ef7e77?auto=format&fit=crop&q=80&w=2000" alt="Luxury Modern Kitchen" className="w-full h-full object-cover brightness-[0.6] contrast-110" data-id="element-385" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" data-id="element-386" />
        </div>

        <div className="max-w-7xl mx-auto w-full px-6 relative z-10 text-center" data-id="element-387">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1.2,
          ease: [0.22, 1, 0.36, 1]
        }} className="max-w-5xl mx-auto" data-id="element-388">
            <h1 className="text-4xl md:text-7xl font-black text-white leading-[0.9] tracking-tight" data-id="element-389">
              BEAUTIFULLY CRAFTED, <br data-id="element-390" />
              <span className="text-white/90" data-id="element-391">DESIGNED TO LAST</span>
            </h1>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.5
          }} className="mt-12" data-id="element-392">
              <Link to="/showroom" className="inline-block bg-[#6B0D24] text-white px-12 py-5 font-black uppercase tracking-[0.2em] text-sm hover:bg-[#5A0B1E] transition-all duration-300 shadow-2xl" data-id="element-393">
                View Our Works
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </header>

      <MarqueeGallery data-id="element-394" />

      {/* Welcome Section */}
      <section className="bg-white overflow-hidden" data-id="element-395">
        <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center" data-id="element-396">
          <div className="flex-1 p-8 md:p-16 lg:p-20 space-y-6" data-id="element-397">
            <div className="space-y-1" data-id="element-398">
              <h2 className="text-3xl md:text-4xl font-light text-charcoal/40" data-id="element-399">
                Welcome to
              </h2>
              <h3 className="text-5xl md:text-7xl font-black text-[#6B0D24] leading-none tracking-tighter" data-id="element-400">
                NK Resurfacers
              </h3>
            </div>

            <div className="space-y-4 text-charcoal/80 leading-relaxed text-base" data-id="element-401">
              <p className="font-bold" data-id="element-402">
                Thank you for visiting NK Resurfacers for your kitchen cabinet,
                flooring and bathroom remodeling.
              </p>
              <p data-id="element-403">
                We do our best to make sure our customers get the best deal,
                with high quality of workmanship. We specialize in both cabinet
                refacing and cabinet refinishing. We repair, modify or replace
                the existing cabinet to meet modern standards. Our new cabinets
                are all solid wood and plywood. NO particle board.
              </p>
              <p data-id="element-404">
                NK Resurfacers offers a wide variety of brand name cabinetry and
                flooring to meet both residential and commercial needs, from
                classic and elegant to sleek and contemporary.
              </p>
            </div>

            <Link to="/about" className="inline-block bg-[#6B0D24] text-white px-10 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#5A0B1E] transition-colors" data-id="element-405">
              Read More
            </Link>
          </div>

          <div className="flex-1 h-[400px] lg:h-[800px] w-full" data-id="element-406">
            <img src="https://cdn.magicpatterns.com/uploads/aj82HdUgjrEekn9C99Y12s/home-img-1.png" alt="Modern Bathroom Design" className="w-full h-full object-cover" data-id="element-407" />
          </div>
        </div>
      </section>

      {/* Happy Clients Section */}
      <section className="relative min-h-[500px] flex items-center justify-center overflow-hidden py-20" data-id="element-408">
        {/* Background Image */}
        <div className="absolute inset-0 z-0" data-id="element-409">
          <img src="https://cdn.magicpatterns.com/uploads/mzbLFwnMbvcjEXRfABUGXF/image.png" alt="Happy Clients Background" className="w-full h-full object-cover brightness-[0.5]" data-id="element-410" />
          <div className="absolute inset-0 bg-black/20" data-id="element-411" />
        </div>

        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center text-white" data-id="element-412">
          <h2 className="text-3xl md:text-5xl font-light mb-10 drop-shadow-lg" data-id="element-413">
            Happy Clients
          </h2>

          <div className="relative" data-id="element-414">
            <AnimatePresence mode="wait" data-id="element-415">
              <motion.div key={currentTestimonial} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -20
            }} transition={{
              duration: 0.5
            }} className="space-y-8 bg-black/10 backdrop-blur-[2px] p-8 md:p-12 rounded-2xl" data-id="element-416">
                <div className="flex justify-center" data-id="element-417">
                  <div className="w-32 h-32 rounded-full border-4 border-white/20 overflow-hidden" data-id="element-418">
                    <img src={TESTIMONIALS[currentTestimonial].image} alt={TESTIMONIALS[currentTestimonial].name} className="w-full h-full object-cover" data-id="element-419" />
                  </div>
                </div>

                <h3 className="text-xl font-bold drop-shadow-md" data-id="element-420">
                  {TESTIMONIALS[currentTestimonial].name}
                </h3>

                <p className="text-base md:text-lg leading-relaxed text-white max-w-2xl mx-auto italic drop-shadow-md" data-id="element-421">
                  "{TESTIMONIALS[currentTestimonial].text}"
                </p>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-12 p-3 border border-white/20 hover:bg-white/10 transition-colors" aria-label="Previous testimonial" data-id="element-422">
              <ChevronLeft size={24} data-id="element-423" />
            </button>
            <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-12 p-3 border border-white/20 hover:bg-white/10 transition-colors" aria-label="Next testimonial" data-id="element-424">
              <ChevronRight size={24} data-id="element-425" />
            </button>
          </div>
        </div>
      </section>

      {/* Professional Services Section */}
      <section className="py-20 px-6 bg-white overflow-hidden" data-id="element-426">
        <div className="max-w-7xl mx-auto" data-id="element-427">
          <div className="text-center mb-16 space-y-1" data-id="element-428">
            <span className="text-charcoal/40 text-lg font-light" data-id="element-429">
              Professional
            </span>
            <h2 className="text-5xl md:text-7xl font-black text-[#6B0D24] leading-none tracking-tighter" data-id="element-430">
              Services
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-20" data-id="element-431">
            <div className="flex-1 w-full h-[400px] lg:h-[500px]" data-id="element-432">
              <img src="https://cdn.magicpatterns.com/uploads/kZ5td5G16ZZVkNFgUMDk7L/kitchen.png" alt="Professional Kitchen Services" className="w-full h-full object-cover shadow-2xl" data-id="element-433" />
            </div>

            <div className="flex-1 space-y-6" data-id="element-434">
              <h3 className="text-3xl md:text-4xl font-black text-[#6B0D24] uppercase tracking-tight" data-id="element-435">
                Kitchen
              </h3>
              <p className="text-lg text-charcoal/70 leading-relaxed max-w-xl" data-id="element-436">
                Products range from furniture quality kitchen or bath cabinetry
                with more than 500,000 combinations of door styles, wood species
                and finishes to Texas made unfinished custom cabinets at
                affordable prices.
              </p>
              <Link to="/process" className="inline-block bg-[#6B0D24] text-white px-12 py-4 font-black uppercase tracking-[0.2em] text-xs hover:bg-[#5A0B1E] transition-colors" data-id="element-437">
                Read More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Projects Section */}
      <section className="py-20 px-6 bg-alabaster" data-id="element-438">
        <div className="max-w-7xl mx-auto" data-id="element-439">
          <div className="text-center mb-16 space-y-1" data-id="element-440">
            <span className="text-charcoal/40 text-lg font-light" data-id="element-441">Latest</span>
            <h2 className="text-5xl md:text-7xl font-black text-[#6B0D24] leading-none tracking-tighter" data-id="element-442">
              Projects
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-443">
            {PROJECTS.map((project, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className={`group cursor-pointer ${idx >= 3 ? 'lg:col-span-1 md:col-span-1' : ''}`} data-id="element-444">
                <div className="relative h-[500px] overflow-hidden border border-charcoal/10 bg-white" data-id="element-445">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" data-id="element-446" />
                  <div className="absolute inset-0 bg-black/20 group-hover:bg-[#6B0D24]/40 transition-colors duration-500" data-id="element-447" />

                  <div className="absolute bottom-0 left-0 w-full p-8 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500" data-id="element-448">
                    <p className="text-[10px] font-black uppercase tracking-[0.3em] mb-2 text-white/70" data-id="element-449">
                      {project.location}
                    </p>
                    <h3 className="text-2xl font-black uppercase tracking-tight leading-none" data-id="element-450">
                      {project.title}
                    </h3>
                  </div>
                </div>
              </motion.div>)}
          </div>

          <div className="mt-16 text-center" data-id="element-451">
            <Link to="/showroom" className="inline-block border-2 border-[#6B0D24] text-[#6B0D24] px-12 py-4 font-black uppercase tracking-[0.2em] text-xs hover:bg-[#6B0D24] hover:text-white transition-all duration-300" data-id="element-452">
              View All Projects
            </Link>
          </div>
        </div>
      </section>

      <TransformationSlider data-id="element-453" />
    </Layout>;
}